<template>
  <layout-no-menu>
    <ion-header>
      <ion-toolbar color="primary">
        <ion-buttons slot="start">
          <ion-icon name="arrow-round-back" size="large" @click="back"></ion-icon>
        </ion-buttons>
        <ion-title>Pay</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <ion-item lines="none" v-if="info">
        <ion-label text-wrap>
          <h1>{{ info.name }}</h1>
          <p v-if="info.allowPayment">{{ money(unitPriceCents) }} per player</p>
        </ion-label>
      </ion-item>

      <ion-item v-if="info && !info.allowPayment">
        <ion-label text-wrap><p>Online payments are not set up for this tournament.</p></ion-label>
      </ion-item>

      <template v-if="info && info.allowPayment">
        <ion-item v-if="cancelled" lines="none">
          <ion-label text-wrap color="warning"><p>Checkout was cancelled. No charge was made.</p></ion-label>
        </ion-item>

        <!-- my players (logged in) -->
        <template v-if="user && myPlayers.length">
          <ion-list-header><ion-label>Your players</ion-label></ion-list-header>
          <ion-list>
            <ion-item v-for="player in myPlayers" :key="'mine-' + player.playerId">
              <ion-checkbox slot="start" :checked="selectedIds.includes(player.playerId)" :disabled="player.paid === true"
                @ionChange="toggle(player.playerId, $event)"></ion-checkbox>
              <ion-label>
                <h2>{{ player.firstName }} {{ player.lastName }}</h2>
                <p v-if="player.team">{{ player.team }}</p>
              </ion-label>
              <ion-note slot="end" :color="player.paid ? 'success' : 'medium'">{{ player.paid ? 'Paid' : money(unitPriceCents) }}</ion-note>
            </ion-item>
          </ion-list>
        </template>

        <!-- anyone else -->
        <ion-list-header><ion-label>{{ user && myPlayers.length ? 'Pay for another player' : 'Who are you paying for?' }}</ion-label></ion-list-header>
        <ion-item>
          <ion-searchbar :value="query" placeholder="Player or team name" debounce="300"
            @ionInput="onQuery($event.target.value)" @ionChange="onQuery($event.target.value)"></ion-searchbar>
        </ion-item>
        <ion-list>
          <template v-for="player in results">
            <ion-item :key="player.playerId" v-if="!isMine(player.playerId)">
              <ion-checkbox slot="start" :checked="selectedIds.includes(player.playerId)" :disabled="player.paid === true"
                @ionChange="toggle(player.playerId, $event)"></ion-checkbox>
              <ion-label>
                <h2>{{ player.firstName }} {{ player.lastName }}</h2>
                <p><span v-if="player.grade">Grade {{ player.grade }}</span><span v-if="player.grade && player.team"> • </span><span v-if="player.team">{{ player.team }}</span></p>
              </ion-label>
              <ion-note v-if="isRecorder" slot="end" :color="player.paid ? 'success' : 'danger'">{{ player.paid ? 'Paid' : 'Unpaid' }}</ion-note>
            </ion-item>
            <ion-item v-if="isRecorder && player.paid === false" :key="'cash-' + player.playerId" lines="full">
              <ion-label><p>In-person payment</p></ion-label>
              <ion-button slot="end" size="small" fill="outline" :disabled="busy" @click.stop="markPaid(player)">Cash</ion-button>
            </ion-item>
          </template>
          <ion-item v-if="!searching && searched && results.length === 0">
            <ion-label text-wrap><p>No players match. <a href="#" v-if="info.allowRegistration" @click.prevent="register">Register a new player</a></p></ion-label>
          </ion-item>
        </ion-list>

        <template v-if="!user">
          <ion-list-header><ion-label>Receipt email</ion-label></ion-list-header>
          <ion-item>
            <ion-input type="email" inputmode="email" placeholder="you@example.com" :value="email" @input="email = $event.target.value"></ion-input>
          </ion-item>
        </template>

        <ion-item v-if="info.testMode" lines="none">
          <ion-label text-wrap color="warning"><p>Test mode: no real charge is made.</p></ion-label>
        </ion-item>
        <ion-item lines="none">
          <ion-label>{{ selectedIds.length }} × {{ money(unitPriceCents) }}<p v-if="serviceFeeCents">+ {{ money(serviceFeeCents) }} processing fee</p></ion-label>
          <ion-note slot="end" color="dark"><strong>Total {{ money(totalCents) }}</strong></ion-note>
        </ion-item>
        <ul style="color: red" v-if="error"><li>{{ error }}</li></ul>
        <div class="ion-padding-horizontal">
          <ion-button expand="block" :disabled="selectedIds.length === 0 || !emailValid || busy" @click="pay">
            {{ busy ? 'Opening checkout…' : 'Pay ' + money(totalCents) }}
          </ion-button>
          <p class="ion-text-center" style="font-size: 12px; color: gray;">Secure checkout by {{ info.provider || 'our payment provider' }}</p>
        </div>
      </template>

      <template v-if="isRecorder && info">
        <ion-list-header color="light"><ion-label>Host tools</ion-label></ion-list-header>
        <div class="ion-text-center" v-if="qrDataUrl">
          <img :src="qrDataUrl" alt="QR code for the pay page" style="width: 220px; height: 220px;" />
          <p style="font-size:12px;color:gray">Share this page</p>
        </div>
        <ion-item>
          <ion-input readonly :value="payUrl"></ion-input>
          <ion-button slot="end" size="small" fill="outline" @click="copyLink">{{ copied ? 'Copied' : 'Copy link' }}</ion-button>
        </ion-item>
        <ion-item button detail="true" @click="$router.push({ name: 'Payments', params: { tournament: tournamentId } })">
          <ion-icon slot="start" name="card"></ion-icon>
          <ion-label>Payments dashboard</ion-label>
        </ion-item>
      </template>
      <div style="height:120px"></div>
    </ion-content>
  </layout-no-menu>
</template>

<script>
import QRCode from "qrcode";
import PaymentAPI from "@/services/PaymentAPI";
import Authentication from "@/services/Authentication";
const paymentAPI = new PaymentAPI();
const authentication = new Authentication();

export default {
  name: "Pay",
  data() {
    const user = authentication.getUser();
    return {
      tournamentId: this.$route.params.tournament,
      user: user && user.userName ? user : null,
      info: null,
      myPlayers: [],
      query: "",
      results: [],
      searching: false,
      searched: false,
      selectedIds: [],
      email: user && user.email ? user.email : "",
      busy: false,
      error: "",
      cancelled: this.$route.query.cancelled === "1",
      qrDataUrl: "",
      copied: false,
    };
  },
  computed: {
    isRecorder() {
      const roles = (this.user && this.user.roles) || [];
      const id = this.tournamentId;
      return roles.includes(`${id}-Admin`) || roles.includes(`${id}-Recorder`) || roles.includes("0-SuperAdmin");
    },
    unitPriceCents() {
      return Math.round((Number(this.info ? this.info.price : 0) || 0) * 100);
    },
    baseCents() {
      return this.selectedIds.length * this.unitPriceCents;
    },
    serviceFeeCents() {
      if (!this.info) return 0;
      return this.selectedIds.length * (Number(this.info.serviceFeeCentsPerPlayer) || 0);
    },
    totalCents() {
      return this.baseCents + this.serviceFeeCents;
    },
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email || "");
    },
    payUrl() {
      return `${window.location.origin}/${this.tournamentId}/Pay`;
    },
  },
  methods: {
    money(cents) {
      return PaymentAPI.money(cents);
    },
    back() {
      this.$router.push({ name: "Tournament", params: { tournament: this.tournamentId } });
    },
    register() {
      this.$router.push({ name: "SignUpRegister", params: { tournament: this.tournamentId } });
    },
    isMine(playerId) {
      return this.myPlayers.some(p => p.playerId === playerId);
    },
    onQuery(value) {
      this.query = value || "";
      this.search();
    },
    async search() {
      const q = this.query.trim();
      if (q.length < 2 && !this.user) {
        this.results = [];
        this.searched = false;
        return;
      }
      this.searching = true;
      try {
        this.results = await paymentAPI.searchPlayers(this.tournamentId, q);
        this.searched = true;
      } catch (e) {
        this.error = typeof e === "string" ? e : "Search failed.";
      } finally {
        this.searching = false;
      }
    },
    toggle(playerId, event) {
      const checked = event && event.detail && event.detail.checked === true;
      const exists = this.selectedIds.includes(playerId);
      if (checked && !exists) this.selectedIds = [...this.selectedIds, playerId];
      else if (!checked && exists) this.selectedIds = this.selectedIds.filter(id => id !== playerId);
    },
    async pay() {
      if (this.selectedIds.length === 0 || this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        const result = this.user
          ? await paymentAPI.checkout(this.tournamentId, this.selectedIds, { allowAnyPlayer: true, source: "pay" })
          : await paymentAPI.checkoutGuest(this.tournamentId, this.selectedIds, this.email);
        if (result && result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
          return;
        }
        this.error = "Could not start checkout.";
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not start checkout.";
      } finally {
        this.busy = false;
      }
    },
    async markPaid(player) {
      try {
        await this.$confirm(`Record a cash payment of ${this.money(this.unitPriceCents)} for ${player.firstName} ${player.lastName}?`);
      } catch (e) {
        return;
      }
      this.busy = true;
      this.error = "";
      try {
        await paymentAPI.markPaid(player.playerId, true);
        await this.search();
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not mark paid.";
      } finally {
        this.busy = false;
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.payUrl);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (e) {
        this.error = "Copy failed; select the link and copy it.";
      }
    },
    async loadData() {
      this.error = "";
      try {
        this.info = await paymentAPI.payInfo(this.tournamentId);
      } catch (e) {
        this.error = typeof e === "string" ? e : "Could not load the tournament.";
        return;
      }
      if (this.user) {
        try {
          this.myPlayers = await paymentAPI.myPlayers(this.tournamentId);
          // Your own unpaid players are pre-selected; add anyone else from the search.
          this.selectedIds = this.myPlayers.filter(p => p.paid === false).map(p => p.playerId);
        } catch (e) {
          this.myPlayers = [];
        }
        this.search(); // logged-in users can browse the roster
      }
      if (this.isRecorder) {
        try {
          this.qrDataUrl = await QRCode.toDataURL(this.payUrl, { width: 220, margin: 1 });
        } catch (e) {
          console.warn("QR failed", e);
        }
      }
    },
  },
  created() {
    this.loadData();
  },
};
</script>
